/**
 * Zone-filter Appstle subscription-plan widgets inside the product quick-view
 * modal. Mirrors the PDP filter from sections/postcode-gate-product.liquid, but
 * scoped to <quick-view> so it works on collection pages, the homepage, or
 * anywhere else the modal can open.
 *
 * Reads zone + appstleGroupGid from localStorage (yf_delivery_selection) and
 * the product's selling_plan_groups from the Appstle standalone span's
 * data-product-data attribute (already rendered by snippets/buy-buttons.liquid).
 *
 * Runs on:
 *   - quickview:loaded custom event (fired after modal HTML injects)
 *   - DOM mutations inside any open <quick-view> (Appstle widget injects async)
 */
(function () {
  'use strict';
  if (window.__yfModalZoneFilterReady) return;
  window.__yfModalZoneFilterReady = true;

  var SELECTION_KEY = 'yf_delivery_selection';

  function LOG() {
    try { console.log.apply(console, ['[MZF]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  function getSelection() {
    try {
      var raw = localStorage.getItem(SELECTION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  // The zone's appstleGroupGid might be stored as either the full GID
  // ("gid://shopify/SellingPlanGroup/abc123") or just the hash ("abc123").
  // Same loose match as sections/postcode-gate-product.liquid::getAllowedPlanIds.
  function getAllowedPlanIds(sellingPlanMap, groupGid) {
    if (!groupGid || !sellingPlanMap) return null;
    if (sellingPlanMap[groupGid]) return sellingPlanMap[groupGid];
    var suffix = String(groupGid).replace(/.*\//, '');
    for (var key in sellingPlanMap) {
      if (sellingPlanMap.hasOwnProperty(key) && key.indexOf('/' + suffix) !== -1) {
        return sellingPlanMap[key];
      }
    }
    return null;
  }

  // Build { "gid://shopify/SellingPlanGroup/<id>": [planId1, planId2, ...] }
  // from the product JSON embedded in Appstle's standalone-selector span.
  function buildPlanMap(modal) {
    var map = {};
    var sources = modal.querySelectorAll('[data-product-data]');
    for (var i = 0; i < sources.length; i++) {
      var raw = sources[i].getAttribute('data-product-data');
      if (!raw) continue;
      try {
        var data = JSON.parse(raw);
        if (!data || !data.selling_plan_groups) continue;
        data.selling_plan_groups.forEach(function (g) {
          var key = 'gid://shopify/SellingPlanGroup/' + g.id;
          var planIds = (g.selling_plans || []).map(function (sp) { return String(sp.id); });
          if (!map[key]) map[key] = [];
          planIds.forEach(function (id) {
            if (map[key].indexOf(id) === -1) map[key].push(id);
          });
        });
      } catch (e) {}
    }
    return map;
  }

  // True if the modal form contains the hidden lock input for the given lock.
  // Mirrors the detection in sections/postcode-gate-product.liquid — the input
  // is emitted via plain Liquid {%- if -%} in buy-buttons.liquid so it's a
  // 100% reliable signal regardless of how Shopify renders boolean form-tag
  // kwargs.
  function findFormForWidget(widget) {
    return widget && widget.closest ? widget.closest('form') : null;
  }
  function formIsSubOnly(form) {
    return !!(form && form.querySelector('input[name="properties[_yf_sub_only]"]'));
  }
  function formIsOneTimeOnly(form) {
    return !!(form && form.querySelector('input[name="properties[_yf_one_time_only]"]'));
  }

  // Read the YF allocation data the native price panel emits (see
  // snippets/product-price.liquid display_mode='panel') and render the
  // Subscribe & Save discount price + strikethrough + badge into the modal's
  // Add-to-Cart button. Mirrors what Appstle injects on the PDP — used here
  // because Appstle's overlay listeners are bound via its internal jQuery and
  // can't be triggered from outside its closure.
  function renderModalDiscountButton(modal) {
    var allocBlob = modal.querySelector('script[data-yf-allocations]');
    if (!allocBlob) return;
    var allocs;
    try { allocs = JSON.parse(allocBlob.textContent || '{}'); } catch (e) { return; }

    // Pick the plan to display, in order of preference:
    //   1. Shopper's zone defaultPlanGid (yf_delivery_selection in localStorage)
    //   2. The panel's server-rendered default plan id (data-default-plan-id)
    //   3. The first key in the allocations object
    // Mirrors yf-price-panel.js zone-or-default fallback so the button matches
    // whatever the panel above it displays.
    var planId = null;
    var sel = getSelection();
    if (sel && sel.defaultPlanGid) {
      planId = String(sel.defaultPlanGid).split('/').pop();
    }
    if (!planId || !allocs[planId]) {
      var defaultRow = modal.querySelector('[data-default-plan-id]');
      if (defaultRow) planId = defaultRow.getAttribute('data-default-plan-id');
    }
    if (!planId || !allocs[planId]) {
      var keys = Object.keys(allocs);
      planId = keys[0] || null;
    }
    if (!planId) return;
    var match = allocs[planId];
    if (!match || !match.price) return;

    modal.querySelectorAll('form').forEach(function (form) {
      if (formIsOneTimeOnly(form)) return;
      var bb = form.querySelector('[id^="BuyButtonPrice-"]');
      if (!bb) return;
      // Cache the original BuyButtonPrice contents the first time we see it.
      // The original holds the variant's base price formatted for display
      // — we strikethrough that text alongside the discount price.
      if (!bb.hasAttribute('data-yf-original-html')) {
        bb.setAttribute('data-yf-original-html', bb.innerHTML);
      }
      var pb = bb.querySelector('product-buy-price');
      var basePriceHtml = pb ? pb.innerHTML : '';
      if (!basePriceHtml) return;

      var pctText = match.isPct
        ? 'SUBSCRIBE &amp; SAVE ' + match.pct + '%'
        : 'SUBSCRIBE &amp; SAVE';

      var html =
        '<span class="relative">&nbsp;-&nbsp;</span>' +
        '<span class="yf-btn-price__sub">' + match.price + '</span>' +
        '<s class="yf-btn-price__base">' + basePriceHtml + '</s>' +
        '<span class="yf-btn-price__badge">' + pctText + '</span>';
      if (bb.innerHTML !== html) {
        bb.innerHTML = html;
        bb.setAttribute('data-yf-discount-rendered', '1');
      }
    });
  }

  function filterAppstleInModal(modal, allowedSet) {
    // Card widget — radios inside .appstle_input_wrapper
    var widgets = new Map ? new Map() : null;
    var wrappers = modal.querySelectorAll('.appstle_sub_widget .appstle_input_wrapper');
    // Group by widget so we can re-select the first visible plan if the
    // previously-selected card gets hidden.
    var widgetList = [];
    var byWidget = {};
    wrappers.forEach(function (wrapper) {
      var widget = wrapper.closest('.appstle_sub_widget');
      if (!widget) return;
      var key = widget.id || 'w-' + widgetList.length;
      if (!byWidget[key]) { byWidget[key] = { widget: widget, items: [] }; widgetList.push(key); }
      byWidget[key].items.push(wrapper);
    });

    widgetList.forEach(function (key) {
      var entry = byWidget[key];
      var firstVisible = null;
      var hasVisibleSelected = false;
      var selectedWasHidden = false;

      // Tag-aware lock for this widget: subOnly hides the one-time radio,
      // oneTimeOnly hides every plan radio (one-time stays selected).
      var hostForm = findFormForWidget(entry.widget);
      var subOnly = formIsSubOnly(hostForm);
      var oneTimeOnly = formIsOneTimeOnly(hostForm);

      entry.items.forEach(function (wrapper) {
        var radio = wrapper.querySelector('input[type="radio"][name="selling_plan"]');
        if (!radio) return;
        var val = radio.value;
        if (!val) {
          // One-time radio. For subscription-only products, hide it so the
          // shopper can't pick a one-time purchase.
          if (subOnly) {
            wrapper.style.display = 'none';
            radio.disabled = true;
            if (radio.checked) { radio.checked = false; selectedWasHidden = true; }
          } else {
            wrapper.style.display = '';
            radio.disabled = false;
            if (radio.checked) hasVisibleSelected = true;
          }
          return;
        }
        // For one-time-only products: hide every plan radio. The widget itself
        // is also hidden in the post-filter pass below, but disabling the
        // radios is belt-and-braces in case the widget container is not hidden.
        if (oneTimeOnly) {
          wrapper.style.display = 'none';
          radio.disabled = true;
          if (radio.checked) { radio.checked = false; selectedWasHidden = true; }
          return;
        }
        // With no zone set (allowedSet === null) no plan is valid yet -- every
        // plan is hidden and yf-suburb-required.js shows a "Please tell us
        // your suburb" box in its place. Showing them all let shoppers pick
        // another zone's delivery day.
        var keep = allowedSet ? allowedSet.has(val) : false;
        if (keep) {
          wrapper.style.display = '';
          radio.disabled = false;
          if (!firstVisible) firstVisible = radio;
          if (radio.checked) hasVisibleSelected = true;
        } else {
          wrapper.style.display = 'none';
          radio.disabled = true;
          if (radio.checked) { radio.checked = false; selectedWasHidden = true; }
        }
      });

      if (allowedSet && firstVisible && (!hasVisibleSelected || entry.widget.hasAttribute('data-yf-forced-onetime'))) {
        // Zone known: nothing visible selected (cleared while there was no
        // suburb), or one-time was only forced by the no-suburb pass. Select
        // the zone's default plan, else the first visible plan.
        var sel = getSelection();
        var defaultId = sel && sel.defaultPlanGid ? String(sel.defaultPlanGid).replace(/.*\//, '') : '';
        var preferred = null;
        entry.items.forEach(function (wrapper) {
          var r = wrapper.querySelector('input[type="radio"][name="selling_plan"]');
          if (!preferred && r && r.value && r.value === defaultId && wrapper.style.display !== 'none') preferred = r;
        });
        preferred = preferred || firstVisible;
        preferred.checked = true;
        preferred.dispatchEvent(new Event('change', { bubbles: true }));
        entry.widget.removeAttribute('data-yf-forced-onetime');
      } else if (!allowedSet && selectedWasHidden && !hasVisibleSelected) {
        // No suburb yet (every plan hidden): fall back to the one-time card
        // if the product allows it, so a hidden plan isn't submitted.
        var fallback = null;
        entry.items.forEach(function (wrapper) {
          var r = wrapper.querySelector('input[type="radio"][name="selling_plan"]');
          if (!fallback && r && !r.value && !r.disabled && wrapper.style.display !== 'none') fallback = r;
        });
        if (fallback) {
          fallback.checked = true;
          fallback.dispatchEvent(new Event('change', { bubbles: true }));
          entry.widget.setAttribute('data-yf-forced-onetime', '');
        }
      }
    });

    // Legacy selects (Appstle still emits these on some products)
    var selects = modal.querySelectorAll('select');
    selects.forEach(function (select) {
      var hasPlanOpt = false;
      var opts = Array.prototype.slice.call(select.options);
      for (var i = 0; i < opts.length; i++) {
        if (opts[i].value && /^\d{5,}$/.test(opts[i].value)) { hasPlanOpt = true; break; }
      }
      if (!hasPlanOpt) return;

      var hostForm = select.closest('form');
      var subOnly = formIsSubOnly(hostForm);
      var firstVis = null;
      var hasVisSel = false;
      opts.forEach(function (opt) {
        if (!opt.value || opt.value === '0') {
          // Subscription-only: strip the placeholder/one-time option.
          if (subOnly) {
            opt.style.display = 'none';
            opt.disabled = true;
            opt.hidden = true;
            if (opt.selected) opt.selected = false;
          }
          return;
        }
        // No zone selected = no valid plan (see card widget above).
        var keep = allowedSet ? allowedSet.has(opt.value) : false;
        opt.style.display = keep ? '' : 'none';
        opt.disabled = !keep;
        opt.hidden = !keep;
        if (keep) {
          if (!firstVis) firstVis = opt;
          if (opt.selected) hasVisSel = true;
        } else if (opt.selected) {
          opt.selected = false;
        }
      });
      if (!hasVisSel && firstVis) {
        firstVis.selected = true;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // Render the Subscribe & Save discount + strikethrough + badge into the
    // modal's Add-to-Cart button price. Appstle does this on the PDP but
    // doesn't on the dynamically-injected modal — its price-overlay
    // listeners are bound through Appstle's internal (non-global) jQuery so
    // we can't trigger them from outside. Instead we read the same allocation
    // data the YF native price panel uses (script[data-yf-allocations],
    // rendered by product-price.liquid) and replicate the discount layout
    // ourselves. Skipped for one-time-only forms (BuyButtonPrice is hidden
    // entirely below) and sub-only forms (button gets the same treatment as
    // a normal subscribable since both have a default plan to show).
    renderModalDiscountButton(modal);

    // One-time-only modal: hide the entire Appstle widget + the BuyButtonPrice
    // price-display in the Add-to-Cart button. Mirrors the PDP behaviour from
    // sections/postcode-gate-product.liquid::hideAppstleWidgetsForOneTimeOnly,
    // scoped to forms inside this modal so we don't accidentally affect any
    // other widget/button on the page.
    modal.querySelectorAll('.appstle_sub_widget').forEach(function (w) {
      var hostForm = findFormForWidget(w);
      if (!formIsOneTimeOnly(hostForm)) return;
      if (w.style.display !== 'none') {
        w.style.display = 'none';
        w.setAttribute('aria-hidden', 'true');
      }
    });
    modal.querySelectorAll('form').forEach(function (form) {
      if (!formIsOneTimeOnly(form)) return;
      // Strip any selling_plan input/select/radio Appstle silently set.
      form.querySelectorAll('[name="selling_plan"]').forEach(function (el) {
        if (el.tagName === 'SELECT') {
          var blank = Array.from(el.options).find(function (o) { return !o.value; });
          if (blank) blank.selected = true; else el.value = '';
        } else if (el.type === 'radio' || el.type === 'checkbox') {
          if (el.value) { el.checked = false; el.disabled = true; }
        } else {
          el.value = '';
          el.disabled = true;
        }
      });
      // Hide the BuyButtonPrice container so the button doesn't show the
      // Subscribe & Save discount price.
      form.querySelectorAll('[id^="BuyButtonPrice-"]').forEach(function (bb) {
        if (bb.style.display !== 'none') {
          bb.style.display = 'none';
          bb.setAttribute('aria-hidden', 'true');
        }
      });
    });
  }

  function runForModal(modal) {
    // The theme's plan picker (yf-plan-picker.js) filters plans by zone and
    // renders the button price itself.
    if (modal.querySelector('yf-plan-picker')) return;
    // Build the allowed plan-id set from the shopper's zone, OR null when no
    // zone is selected. With null, filterAppstleInModal hides every plan and
    // still applies lock-based filtering (sub-only / one-time-only via hidden
    // line property inputs) — those locks are zone-independent.
    var sel = getSelection();
    var allowedSet = null;
    if (sel && sel.appstleGroupGid) {
      var map = buildPlanMap(modal);
      var allowed = getAllowedPlanIds(map, sel.appstleGroupGid) || [];
      allowedSet = new Set(allowed.map(String));
    }
    filterAppstleInModal(modal, allowedSet);
  }

  function runAll() {
    document.querySelectorAll('quick-view').forEach(function (modal) {
      // Skip modals that haven't been populated yet.
      if (!modal.querySelector('[data-product-data]')) return;
      runForModal(modal);
    });
  }

  // Debounced re-filter so Appstle's async widget injection doesn't
  // spam filter passes per mutation.
  var debounceTimer = null;
  function scheduleRun() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(runAll, 100);
  }

  document.addEventListener('quickview:loaded', function () {
    // Run twice — once after the initial inject (200ms after Appstle fires),
    // once again a bit later to catch any late Appstle DOM changes.
    setTimeout(runAll, 250);
    setTimeout(runAll, 1000);
  });

  // When the customer picks a suburb (in the in-modal gate, the page gate, or
  // any other gate), re-run the filter so the modal's Appstle plans collapse
  // to the chosen zone. Without this, the modal stays showing every plan
  // because the previous runAll() call ran with allowedSet=null (no zone).
  document.addEventListener('yf:zone-changed', function () {
    // Small delay so localStorage write + any DOM reflows settle first.
    setTimeout(runAll, 50);
  });

  // Body-level observer catches Appstle's async widget injection inside any
  // modal. Cheap: we only call runAll when the mutation includes a node that
  // matches an Appstle selector.
  try {
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        if (!added || !added.length) continue;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (!node || node.nodeType !== 1) continue;
          if (
            (node.matches && (
              node.matches('.appstle_sub_widget') ||
              node.matches('.appstle_input_wrapper') ||
              node.matches('[data-product-data]')
            )) ||
            (node.querySelector && node.querySelector('.appstle_sub_widget, .appstle_input_wrapper, [data-product-data]'))
          ) {
            scheduleRun();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  } catch (e) { LOG('observer attach failed', e); }
})();
